<template>
  <div class="trip-settings h-full flex flex-col" :key="language">
    <!-- Vehicle Selector -->
    <div v-if="vehicles.length > 0 && !isInDemoMode" class="p-3 border-b-2 border-gray-200 dark:border-gray-600">
      <VehicleSelector
        :vehicles="vehicles"
        :model-value="selectedVehicleId"
        @update:model-value="$emit('update:vehicleId', $event)"
        @add-vehicle="$emit('add-vehicle')"
      />
    </div>

    <!-- Route Info -->
    <div v-if="stops.length >= 2" class="p-3 border-b-2 border-gray-200 dark:border-gray-600">
      <div class="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-lg p-3 border-2 border-blue-200 dark:border-blue-700">
        <div class="flex items-center gap-2 mb-3">
          <span class="text-xl">🗺️</span>
          <h3 class="text-sm font-bold text-blue-900 dark:text-blue-100">
            {{ t('route') }}
          </h3>
        </div>
        <div class="space-y-3">
          <div class="flex items-center gap-2">
            <span class="text-lg">🏁</span>
            <div class="flex-1 min-w-0">
              <div class="text-xs text-blue-600 dark:text-blue-300 font-semibold">{{ t('start') }}</div>
              <div class="text-sm text-gray-900 dark:text-gray-100 truncate font-medium">{{ stops[0].name }}</div>
              <div class="text-xs text-gray-500 dark:text-gray-400 truncate">{{ stops[0].address }}</div>
            </div>
          </div>
          <div class="flex items-center justify-center text-gray-400">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 14l-7 7m0 0l-7-7m7 7V3"></path>
            </svg>
          </div>
          <div class="flex items-center gap-2">
            <span class="text-lg">🎯</span>
            <div class="flex-1 min-w-0">
              <div class="text-xs text-red-600 dark:text-red-300 font-semibold">{{ t('finish') }}</div>
              <div class="text-sm text-gray-900 dark:text-gray-100 truncate font-medium">{{ stops[stops.length - 1].name }}</div>
              <div class="text-xs text-gray-500 dark:text-gray-400 truncate">{{ stops[stops.length - 1].address }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Trip Summary -->
    <div v-if="calculation && stops.length > 1" class="p-3 flex-1 overflow-y-auto">
      <div class="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 rounded-lg p-3 border-2 border-green-200 dark:border-green-700">
        <div class="flex items-center gap-2 mb-3">
          <span class="text-xl">📊</span>
          <h3 class="text-sm font-bold text-green-900 dark:text-green-100">
            {{ t('tripSummary') }}
          </h3>
        </div>
        <div class="grid grid-cols-2 gap-2 text-xs">
          <div class="bg-white/50 dark:bg-gray-800/50 p-2 rounded-lg">
            <div class="text-gray-600 dark:text-gray-400 mb-1">🛣️ {{ t('distance') }}</div>
            <div class="font-bold text-green-600 dark:text-green-400">{{ Math.round(calculation.totalDistance / 1000) }} km</div>
          </div>
          <div class="bg-white/50 dark:bg-gray-800/50 p-2 rounded-lg">
            <div class="text-gray-600 dark:text-gray-400 mb-1">⏱️ {{ t('duration') }}</div>
            <div class="font-bold text-blue-600 dark:text-blue-400">{{ formatDuration(calculation.totalDuration) }}</div>
          </div>
          <div class="bg-white/50 dark:bg-gray-800/50 p-2 rounded-lg">
            <div class="text-gray-600 dark:text-gray-400 mb-1">💰 {{ t('fuelCost') }}</div>
            <div class="font-bold text-orange-600 dark:text-orange-400">{{ formatCurrency(convertedFuelCost, currency) }}</div>
          </div>
          <div class="bg-white/50 dark:bg-gray-800/50 p-2 rounded-lg">
            <div class="text-gray-600 dark:text-gray-400 mb-1">📍 {{ t('stopsCount') }}</div>
            <div class="font-bold text-purple-600 dark:text-purple-400">{{ stops.length }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- No stops message -->
    <div v-if="stops.length === 0" class="flex-1 flex items-center justify-center p-6">
      <div class="text-center">
        <div class="text-5xl mb-4">🗺️</div>
        <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
          {{ t('noStops') }}
        </h3>
        <p class="text-gray-600 dark:text-gray-400 text-sm">
          {{ t('createFirstTrip') }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { Stop, TripCalculation, Vehicle } from '@/types'
import { useI18n } from '@/i18n'
import { useSettingsStore } from '@/stores/settings'
import { useAuthStore } from '@/stores/auth'
import { storeToRefs } from 'pinia'
import { formatCurrency, convertCurrency } from '@/utils/currency'
import VehicleSelector from './VehicleSelector.vue'

interface Props {
  stops: Stop[]
  calculation?: TripCalculation | null
  vehicles?: Vehicle[]
  selectedVehicleId?: string | null
}

const props = withDefaults(defineProps<Props>(), {
  calculation: null,
  vehicles: () => [],
  selectedVehicleId: null
})

defineEmits<{
  'update:vehicleId': [vehicleId: string | null]
  'add-vehicle': []
}>()

const { t } = useI18n()
const settingsStore = useSettingsStore()
const authStore = useAuthStore()
const { language, currency } = storeToRefs(settingsStore)

const isInDemoMode = computed(() => !authStore.isAuthenticated)

const convertedFuelCost = computed(() => {
  if (!props.calculation?.fuelCost) return 0
  return convertCurrency(props.calculation.fuelCost, 'EUR', currency.value)
})

function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`
  }
  return `${minutes}m`
}
</script>

<style scoped>
.trip-settings {
  background: var(--ion-background-color, #fff);
}
</style>
